import { AiAccountLimits, AiAccountLimitsBudgetPeriodEnum, AiTargetType } from '../../api';
import { BaseButton } from '../base/button/button';
import { Toggle } from '../base/toggle/toggle';
import { mdiPlus, mdiRobotOutline, mdiAlertCircle } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import {
    AiVisibility,
    findAgent,
    humanSeconds,
    isAgentReady,
    OPERATIONS,
    profileVisibility,
} from '../../services/ai/ai.derive';
import { validateConfiguration } from '../../services/ai/ai.form';
import { AiStore } from '../../stores/ai/ai.store';
import { NumberField, SelectField, TargetTag } from './ai-fields';
import * as S from './ai.styles';

const PROFILE_COLUMNS =
    'minmax(160px,1.3fr) 84px minmax(180px,1.6fr) minmax(120px,1fr) 100px minmax(130px,1fr) minmax(150px,1.1fr) 56px 72px';

const PERIODS: Array<{ value: AiAccountLimits['budgetPeriod']; label: string }> = [
    { value: AiAccountLimitsBudgetPeriodEnum.DAILY, label: 'Daily (1d)' },
    { value: AiAccountLimitsBudgetPeriodEnum.WEEKLY, label: 'Weekly (7d)' },
    { value: AiAccountLimitsBudgetPeriodEnum.MONTHLY, label: 'Monthly (1mo)' },
];

function visibilityView(v: AiVisibility): { label: string; tone: S.TagTone } {
    if (v.state === 'available') return { label: 'Available', tone: 'success' };
    if (v.state === 'unknown') return { label: 'Unknown · agents not loaded', tone: 'neutral' };
    switch (v.reason) {
        case 'account-off':
            return { label: 'Hidden · account off', tone: 'neutral' };
        case 'profile-disabled':
            return { label: 'Hidden · disabled', tone: 'neutral' };
        case 'agent-unavailable':
            return { label: 'Hidden · agent unavailable', tone: 'attention' };
    }
}

interface IConfigTabProps {
    store: AiStore;
    onEditProfile: (index: number) => void;
}

export const ConfigTab = observer(({ store, onEditProfile }: IConfigTabProps): React.ReactElement => {
    const { draft, saved, agents } = store;

    if (store.loadState === 'loading' || store.loadState === 'idle') {
        return <S.Centered>Loading configuration…</S.Centered>;
    }
    if (store.loadState === 'demo') {
        // NS-14: no request is made in demo mode and nothing real may render.
        return (
            <S.Centered>
                <S.CenteredTitle>Unavailable in demo mode</S.CenteredTitle>
                The AI configuration is account data, so it is not shown while demo mode is on.
            </S.Centered>
        );
    }
    if (store.loadState === 'error') {
        // NS-3: a failed request is "unknown", never "not configured".
        return (
            <S.Centered>
                <Icon path={mdiAlertCircle} size={1} />
                <S.CenteredTitle>The AI configuration could not be loaded</S.CenteredTitle>
                {store.loadError} — the state of this account is unknown.
                <S.Button variant="outlined" onClick={(): void => void store.load()}>
                    Retry
                </S.Button>
            </S.Centered>
        );
    }
    if (!draft) {
        return (
            <S.Centered>
                <Icon path={mdiRobotOutline} size={1.2} />
                <S.CenteredTitle>Not configured</S.CenteredTitle>
                No AI configuration for this account in this environment. Key issuance is denied (AI_NOT_CONFIGURED).
                <S.Button variant="primary" onClick={store.createConfiguration}>
                    <S.ButtonContent>
                        <Icon path={mdiPlus} size={0.75} />
                        Create configuration
                    </S.ButtonContent>
                </S.Button>
            </S.Centered>
        );
    }

    const errors = validateConfiguration(draft, agents);
    const visibility = draft.profiles.map(p => profileVisibility(p, draft, agents));
    const available = visibility.filter(v => v.state === 'available').length;
    const unknown = visibility.filter(v => v.state === 'unknown').length;
    const accountStatus = !saved ? 'Not saved' : store.syncBlocked ? 'Sync failed · blocked' : saved.enabled ? 'Issuing' : 'Issuance off';

    const setLimit = (key: keyof AiAccountLimits) => (value: unknown): void =>
        store.editDraft(d => {
            (d.accountLimits as unknown as Record<string, unknown>)[key] = value;
        });

    return (
        <S.Stack>
            <S.TwoUp>
                <S.Card>
                    <S.CardHead>
                        <S.Grow>
                            <S.CardTitle>Account issuance</S.CardTitle>
                            <S.CardSub>Off stops new keys. Existing keys stay valid until expiry or revocation.</S.CardSub>
                        </S.Grow>
                        <Toggle
                            aria-label="Account issuance"
                            value={draft.enabled}
                            onChange={(v): void =>
                                store.editDraft(d => {
                                    d.enabled = v;
                                })
                            }
                        />
                    </S.CardHead>
                    <S.Facts>
                        <S.Fact>
                            <S.FactLabel>Status</S.FactLabel>
                            <S.FactValue>{accountStatus}</S.FactValue>
                        </S.Fact>
                        <S.Fact>
                            <S.FactLabel>Client-visible profiles</S.FactLabel>
                            <S.FactValue>
                                {available} of {draft.profiles.length}
                                {unknown > 0 && ` · ${unknown} unknown`}
                            </S.FactValue>
                        </S.Fact>
                    </S.Facts>
                </S.Card>

                <S.Card>
                    <div>
                        <S.CardTitle>Account limits</S.CardTitle>
                        <S.CardSub>Shared by every profile and key. Never reset by issuance, expiry, or revocation.</S.CardSub>
                    </div>
                    <S.FormGrid>
                        <NumberField
                            label="Budget (USD)"
                            min={0}
                            step={1}
                            value={draft.accountLimits.maxBudgetUsd}
                            error={errors.limits.maxBudgetUsd}
                            onChange={setLimit('maxBudgetUsd')}
                        />
                        <SelectField
                            label="Budget period"
                            value={draft.accountLimits.budgetPeriod}
                            options={PERIODS}
                            onChange={setLimit('budgetPeriod')}
                        />
                        <NumberField
                            label="Requests / min"
                            min={1}
                            value={draft.accountLimits.requestsPerMinute}
                            error={errors.limits.requestsPerMinute}
                            onChange={setLimit('requestsPerMinute')}
                        />
                        <NumberField
                            label="Tokens / min"
                            min={1}
                            value={draft.accountLimits.tokensPerMinute}
                            error={errors.limits.tokensPerMinute}
                            onChange={setLimit('tokensPerMinute')}
                        />
                    </S.FormGrid>
                </S.Card>
            </S.TwoUp>

            <S.Table>
                <S.TableHead>
                    <S.Grow>
                        <S.CardTitle>Profiles</S.CardTitle>
                        <S.CardSub>Changes apply to keys issued after save. Existing keys keep their issued policy.</S.CardSub>
                    </S.Grow>
                    <S.Button variant="outlined" onClick={(): void => onEditProfile(-1)}>
                        <S.ButtonContent>
                            <Icon path={mdiPlus} size={0.75} />
                            Add profile
                        </S.ButtonContent>
                    </S.Button>
                </S.TableHead>
                <S.Scroll>
                    <S.Row header={true} columns={PROFILE_COLUMNS} min={1020}>
                        <span>Profile</span>
                        <span>Type</span>
                        <span>Target</span>
                        <span>Operations</span>
                        <span>TTL def / max</span>
                        <span>Key limits</span>
                        <span>Client view</span>
                        <span>Enabled</span>
                        <span />
                    </S.Row>
                    {draft.profiles.map((p, i) => {
                        const isAgent = p.targetType === AiTargetType.AGENT;
                        const agent = isAgent ? findAgent(agents, p.agentId) : undefined;
                        const view = visibilityView(visibility[i]);
                        const kl = p.keyLimits ?? { maxBudgetUsd: 0 };
                        const hasError = Object.keys(errors.profiles[i] ?? {}).length > 0;
                        // NS-3: before the catalog loads, or if the agent is missing from it, availability is not
                        // known — say so rather than "UNAVAILABLE".
                        const agentSub = !agents ? 'Agent availability unknown' : !agent ? 'Agent not in catalog' : isAgentReady(agent) ? 'Agent READY' : 'Agent UNAVAILABLE';
                        return (
                            <S.Row key={`${p.id}-${i}`} columns={PROFILE_COLUMNS} min={1020}>
                                <S.Cell>
                                    <S.Strong>
                                        {p.displayName || 'Untitled'}
                                        {hasError && <Icon path={mdiAlertCircle} size={0.65} aria-label="Has errors" />}
                                    </S.Strong>
                                    <S.Mono>{p.id || '—'}</S.Mono>
                                </S.Cell>
                                <span>
                                    <TargetTag type={p.targetType} />
                                </span>
                                <S.Cell>
                                    <S.Mono>
                                        {isAgent ? agent?.invocationModel ?? p.agentId ?? '—' : (p.models ?? []).join(', ') || '—'}
                                    </S.Mono>
                                    <S.Muted>{isAgent ? agentSub : p.defaultModel ? `Default: ${p.defaultModel}` : 'No default'}</S.Muted>
                                </S.Cell>
                                <span>
                                    {(isAgent ? ['CHAT_COMPLETIONS'] : p.operations)
                                        .map(o => OPERATIONS.find(x => x.value === o)?.label ?? o)
                                        .join(', ')}
                                </span>
                                <span>
                                    {humanSeconds(p.defaultLifetimeSeconds)} / {humanSeconds(p.maxLifetimeSeconds)}
                                </span>
                                <span>
                                    ${kl.maxBudgetUsd}
                                    {kl.requestsPerMinute ? ` · ${kl.requestsPerMinute} rpm` : ''}
                                    {kl.tokensPerMinute ? ` · ${kl.tokensPerMinute / 1000}k tpm` : ''}
                                </span>
                                <S.Visibility tone={view.tone}>
                                    <S.Dot tone={view.tone} />
                                    {view.label}
                                </S.Visibility>
                                <span>
                                    <Toggle
                                        aria-label={`Issuance enabled for ${p.displayName || p.id || 'this profile'}`}
                                        value={p.enabled}
                                        onChange={(v): void =>
                                            store.editDraft(d => {
                                                d.profiles[i].enabled = v;
                                            })
                                        }
                                    />
                                </span>
                                {/* An explicit button, not a clickable row: a row cannot take focus, and it would wrap the switch. */}
                                <span>
                                    <BaseButton
                                        variant="outlined"
                                        size="compact"
                                        aria-label={`Edit profile ${p.displayName || p.id || '(unnamed)'}`}
                                        onClick={(): void => onEditProfile(i)}
                                    >
                                        Edit
                                    </BaseButton>
                                </span>
                            </S.Row>
                        );
                    })}
                    {draft.profiles.length === 0 && (
                        <S.Empty>No profiles. No use case is available to this account.</S.Empty>
                    )}
                </S.Scroll>
            </S.Table>
        </S.Stack>
    );
});
